import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../ui';
import { Chip } from '@cowoo/shared/ui-web';
import { useAdminStore } from '../../lib/store';
import { useSignOut } from '../auth/useSignOut';

/**
 * The account menu of 10-web-admin § 3.
 *
 * It carries the SSE state (§ 20). That belongs somewhere always visible but
 * never in the way: a dropped stream means the counts and lists on screen are
 * going stale, which staff should be able to find out, but it is not an error
 * worth a banner while the reconnect is still running.
 */
export function AvatarMenu() {
  const { t } = useTranslation();
  const profile = useAdminStore((state) => state.profile);
  const sse = useAdminStore((state) => state.sse);
  const signOut = useSignOut();
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!profile) return null;

  const live = sse === 'open';

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('admin.shell.account')}
        className="hover:bg-fog rounded-buttonSm flex items-center gap-1 p-0.5"
        data-testid="admin-avatar"
      >
        {/* The board draws the avatar as a person, not as initials; the
            initials stay in the menu header where the name is spelled out. */}
        <span className="bg-teal-tint text-teal-dark rounded-pill flex h-9 w-9 items-center justify-center">
          <Icon name="user" size={18} />
        </span>
        <Chevron />
      </button>

      {open ? (
        <div
          role="menu"
          className="border-line rounded-input absolute right-0 top-11 z-50 w-[260px] border bg-white p-1.5 shadow-lg"
        >
          <div className="border-hairline flex flex-col gap-1 border-b px-2.5 pb-2 pt-2.5">
            {profile.name ? <p className="font-body text-ink text-sm font-bold">{profile.name}</p> : null}
            <p className="font-body text-muted text-xs">{profile.email}</p>
            <span className="mt-1">
              <Chip label={profile.role} tone="teal" />
            </span>
          </div>

          <p className="font-body text-muted flex items-center gap-2 px-2.5 py-1.5 text-xs">
            <span
              className={`rounded-pill h-2 w-2 shrink-0 ${live ? 'bg-success-text' : 'bg-warn-dot'}`}
              aria-hidden
            />
            {t(live ? 'admin.shell.live' : 'admin.shell.liveLost')}
          </p>

          {profile.capabilities.includes('audit.read') ? (
            <Link
              to="/audit"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="font-body text-ink hover:bg-fog rounded-segmentInner flex h-[38px] items-center gap-2.5 px-2.5 text-sm"
            >
              {t('admin.nav.audit')}
            </Link>
          ) : null}

          <p className="font-body text-ink flex h-[38px] items-center justify-between gap-2.5 px-2.5 text-sm">
            {t('admin.shell.totp')}
            <span className="text-muted text-xs">
              {t(profile.totpEnabled ? 'admin.shell.totpOn' : 'admin.shell.totpOff')}
            </span>
          </p>

          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut()}
            className="font-body text-danger-text hover:bg-fog rounded-segmentInner flex h-[38px] w-full items-center gap-2.5 px-2.5 text-sm"
          >
            {t('admin.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Chevron() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden className="block shrink-0">
      <path
        d="m6 9 6 6 6-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-muted"
      />
    </svg>
  );
}
